import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { DateTime } from 'luxon';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { UserDb } from './user-db.service';
import { TelemetryService } from './telemetry.service';
import { DatabaseWrapper, DocWatcher } from './database-wrapper';
import { Log_Workout } from '@cf-shared/data-model/log-workout';
import { resolveUnskippedWorkoutState } from '@cf-shared/data-model/workout-conclusion';
import { LogRecordType, LogRecordState } from '@cf-shared/data-model/user-log';
import { Schedule_ItemType } from '@cf-shared/data-model/schedule';
import { isoTimeToHours } from '@cf-shared/time';
import { ScheduleRuleService } from './schedule-rule.service';
import { NotificationService } from './notification.service';
import { DebugService } from './debug.service';
import { SettingsService } from './settings.service';

export interface SkipWorkoutResult {
  skipped: boolean;
  rescheduledToTomorrow: boolean;
}

@Injectable()
export class WorkoutStateService {
  constructor(
    private userDb: UserDb,
    private alertCtrl: AlertController,
    private telemetry: TelemetryService,
    private scheduleRuleService: ScheduleRuleService,
    private notificationService: NotificationService,
    private debug: DebugService,
    private settings: SettingsService,
  ) {}

  /**
   * The local calendar day a workout sits on, as YYYY-MM-DD.
   *
   * Skipping and un-skipping must agree on this exactly, because it is what the
   * receipt on the created schedule rule is matched by. One helper so the two can
   * never drift apart.
   */
  private getWorkoutDayISO(workout: Log_Workout): string {
    return DateTime.fromISO(workout.time_utc).toLocal().startOf('day').toISODate();
  }

  /**
   * Skip a workout, optionally rescheduling it to tomorrow.
   * Shows a dialog asking if user wants to reschedule if tomorrow has no workout.
   * @param workout The workout entry to skip
   * @returns Result indicating if skipped and if rescheduled
   */
  async skipWorkoutAsync(workout: Log_Workout, watcher: DocWatcher): Promise<SkipWorkoutResult> {
    const workoutTime = DateTime.fromISO(workout.time_utc);
    const skippedWorkoutDayISO = this.getWorkoutDayISO(workout);
    const tomorrowBOD = workoutTime.toLocal().plus({ days: 1 }).startOf('day');
    const tomorrowEOD = tomorrowBOD.endOf('day');
    const tomorrowLogs = await this.userDb.getLogEntriesInRangeAsync(tomorrowBOD, tomorrowEOD, watcher);
    const tomorrowWorkoutLog = tomorrowLogs.find(e => e.type === LogRecordType.Workout);

    let moveWorkoutToNextDay: boolean | undefined = false;

    // if tomorrow has logs but no workout, offer to reschedule
    if (tomorrowLogs.length > 0 && !tomorrowWorkoutLog) {
      moveWorkoutToNextDay = await this.alertCtrl.showAlertAsync<boolean | undefined>({
        message: LL.dayItems.alerts.rescheduleWorkout(),
        buttons: [
          {
            text: LL.common.buttons.yes(),
            role: AlertButtonRole.Primary,
            result: true,
          },
          {
            text: LL.common.buttons.no(),
            result: false,
          },
          {
            text: LL.common.buttons.cancel(),
            role: AlertButtonRole.Cancel,
            result: undefined,
          },
        ],
      });
    }

    // if user cancelled
    if (moveWorkoutToNextDay === undefined) {
      return { skipped: false, rescheduledToTomorrow: false };
    }

    // if user chose to reschedule, add one-time rule for tomorrow
    if (moveWorkoutToNextDay) {
      const schedule = await this.userDb.loadScheduleAsync();
      const nextDayISO = tomorrowBOD.toISODate();
      this.scheduleRuleService.addOneTimeItem({
        rules: schedule.rules,
        itemType: Schedule_ItemType.Workout,
        dateISO: nextDayISO,
        time_hr: isoTimeToHours(workoutTime.toISOTime()),
        // stamp the new rule as ours, so undoing this skip removes this rule and
        // never one the user made themselves. see revertSkipWorkoutAsync.
        skipRescheduledFromDateISO: skippedWorkoutDayISO,
      });
      await this.userDb.saveScheduleAsync(DatabaseWrapper.getTransactionId());
    }

    // clear cache and update entry state
    await this.userDb.clearBacklogCacheAsync();
    workout.state = LogRecordState.Skipped;
    await this.userDb.saveEntryAsync(DatabaseWrapper.getTransactionId(), workout);

    // silence this day's reminder — the user is not training this session. after the save
    // on purpose: a save that throws leaves the workout Scheduled, whose reminder must
    // still arrive. why the call lives here rather than in the shared path every other
    // entry type uses: design-docs/push-notifications.md, "Per-type acted rules".
    void this.notificationService.suppressForEntryAsync(workout, this.debug.getNowLocal());

    this.telemetry.trackEvent('workout_skipped', {});

    return { skipped: true, rescheduledToTomorrow: moveWorkoutToNextDay };
  }

  /**
   * Undo a skip, putting the workout back to the state its own sets imply.
   * Also removes the next-day schedule rule that THIS skip created, if it created
   * one — and nothing else.
   *
   * It used to remove any workout rule dated the next day, which is not the same
   * thing at all: nothing recorded which rule the skip had made, so the undo also
   * deleted an extra workout the user had added for that day themselves, an
   * exclusion rule saying they had moved a workout off that day, and even a
   * recurring training day whose start date happened to fall there. The skip now
   * stamps the rule it creates with the skipped workout's own date, and only a rule
   * carrying that stamp is removed. A skip made before that stamp existed leaves its
   * rule behind, which is the safe direction: an extra workout the user can see and
   * delete, rather than one of theirs quietly disappearing.
   *
   * #4840: this used to write `Scheduled` — "never started" — whatever the
   * workout held, so undoing a skip on a session the user had fully logged left
   * it claiming they had not begun it, and nothing in the app could put that
   * right. `resolveUnskippedWorkoutState` explains why it answers `Incomplete`
   * for a logged workout rather than `Done`, and where the promotion to `Done`
   * happens instead.
   *
   * @param workout The workout entry to revert
   */
  async revertSkipWorkoutAsync(workout: Log_Workout, _watcher: DocWatcher): Promise<void> {
    const workoutTime = DateTime.fromISO(workout.time_utc);
    const tomorrowBOD = workoutTime.toLocal().plus({ days: 1 }).startOf('day');
    const nextDayISO = tomorrowBOD.toISODate();

    // remove the next-day rule this skip created, if it created one
    const schedule = await this.userDb.loadScheduleAsync();
    const oneTimeRule = this.scheduleRuleService.findSkipRescheduledWorkoutRule(
      schedule.rules, nextDayISO, this.getWorkoutDayISO(workout)
    );
    if (oneTimeRule) {
      schedule.rules.splice(schedule.rules.indexOf(oneTimeRule), 1);
      await this.userDb.saveScheduleAsync(DatabaseWrapper.getTransactionId());
    }

    // clear cache and update entry state
    await this.userDb.clearBacklogCacheAsync();
    workout.state = resolveUnskippedWorkoutState(workout);
    await this.userDb.saveEntryAsync(DatabaseWrapper.getTransactionId(), workout);

    // bring back today's reminder the skip silenced, if it has not fired yet — but only for a
    // workout that is back to not started. a workout with logged sets comes back Incomplete, and
    // starting it already silenced its reminder before the skip did. after the save,
    // for the same reason skipWorkoutAsync suppresses after its save. fire-and-forget.
    if (workout.state === LogRecordState.Scheduled) {
      void this.notificationService.unsuppressForEntryAsync(workout, this.settings, () => this.debug.getNowLocal());
    }
  }
}
